import React from "react";
import { motion } from "framer-motion";
import { Video, MessageCircle, Calendar, FileText, ShieldCheck, Clock } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { AppointmentForm } from "../components/AppointmentForm";
import { CLINIC } from "../lib/clinic";

const STEPS = [
  { icon: Calendar, title: "Book your slot", desc: "Fill the form or message us on WhatsApp to confirm a convenient time." },
  { icon: MessageCircle, title: "Quick screening chat", desc: "Brief WhatsApp exchange to understand your concern and share any reports." },
  { icon: Video, title: "Secure video call", desc: "Meet Dr. Avril over a private video link — from your home or office." },
  { icon: FileText, title: "Prescription & plan", desc: "Receive a digital prescription, lifestyle plan and follow-up date." },
];

const OnlineConsultation = () => {
  return (
    <div data-testid="online-consultation-page">
      <PageHeader
        eyebrow="Online Consultation"
        title="Premium care — from the comfort of your home."
        subtitle="A private, secure video consultation with Dr. Avril. Get expert neurology & psychology care, prescription and a treatment plan delivered to your inbox."
        breadcrumbs={["Online Consultation"]}
      />

      {/* Editorial banner */}
      <section className="py-20 md:py-28 bg-paper-soft">
        <div className="container-x">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-brand-navyDark px-8 py-14 md:px-20 md:py-20"
          >
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-7">
                  <span className="w-10 h-px bg-brand-gold" />
                  <span className="text-[11px] tracking-[0.28em] uppercase font-medium text-brand-gold">
                    Live, Private, Secure
                  </span>
                </div>
                <h2 className="heading-serif text-3xl md:text-5xl font-medium text-white leading-[1.08] mb-6">
                  Consult Dr. Avril from anywhere in India.
                </h2>
                <p className="text-stone-300 leading-[1.85] mb-9 max-w-xl">
                  All you need is a phone and a stable internet connection. Private link, no
                  app downloads, prescription on email.
                </p>
                <ul className="space-y-3 mb-9">
                  {[
                    { i: ShieldCheck, t: "End-to-end private video call" },
                    { i: Clock, t: "Flexible morning & evening slots" },
                    { i: FileText, t: "Digital prescription on email" },
                  ].map((x, i) => (
                    <li key={i} className="flex items-center gap-3 text-[14px] text-stone-200">
                      <x.i className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
                      {x.t}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-3">
                  <a href="#book-online" className="inline-flex items-center gap-2 bg-[#FAF9F7] text-brand-navyDark px-7 py-3.5 text-[13px] tracking-[0.06em] uppercase font-medium hover:bg-brand-gold transition-all">
                    <Calendar className="w-3.5 h-3.5" /> Book Now
                  </a>
                  <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-3.5 text-[13px] tracking-[0.06em] uppercase font-medium hover:bg-white hover:text-brand-navyDark transition-all">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5 hidden lg:flex justify-end">
                <span className="heading-serif text-[120px] leading-none text-brand-gold/30 italic">
                  online.
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process timeline */}
      <section className="section-y bg-paper">
        <div className="container-x">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow mb-5">Simple, calming process</div>
            <h2 className="heading-serif text-4xl md:text-5xl font-medium text-brand-navyDark leading-[1.06]">
              How online consultation works.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200">
            {STEPS.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-paper p-8 md:p-10 group hover:bg-paper-soft transition-colors"
              >
                <div className="flex items-baseline gap-4 mb-7">
                  <span className="heading-serif text-2xl text-brand-goldDark font-medium leading-none">0{i + 1}</span>
                  <step.icon className="w-5 h-5 text-brand-tealDark" strokeWidth={1.3} />
                </div>
                <h3 className="heading-serif text-2xl font-medium text-brand-navyDark mb-3">
                  {step.title}
                </h3>
                <p className="text-[14px] text-brand-body leading-[1.8]">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking form */}
      <section id="book-online" className="section-y bg-paper-soft scroll-mt-24">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">Book Online Consultation</div>
            <h2 className="heading-serif text-4xl md:text-5xl font-medium text-brand-navyDark leading-[1.08] mb-7">
              Consult from home — today.
            </h2>
            <p className="text-brand-body leading-[1.85] mb-9 text-[15px]">
              Fill in this short form and we'll confirm your slot on WhatsApp within hours.
              Morning and evening slots available, Monday to Friday.
            </p>
            <div className="space-y-0">
              {CLINIC.hours.map((h, i) => (
                <div key={i} className="flex items-center gap-4 py-4 border-b border-stone-200 last:border-0">
                  <Clock className="w-4 h-4 text-brand-tealDark" strokeWidth={1.5} />
                  <div>
                    <div className="text-[14px] text-brand-navyDark">{h.day}</div>
                    <div className="text-[12px] text-brand-muted">{h.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <AppointmentForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default OnlineConsultation;
